import React from "react";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { normalizeAiReportApiUrl, normalizeAiReportParamInputType, normalizeAiReportParamOptions } from "../../utils/aiReportTypes";
import { normalizeStringList } from "../../utils/assetCardConfig";
import { normalizeVoiceCommandConfig } from "../../services/aiVoiceAssistantApi";
import { DASHBOARD_WIDGET_FONT_SIZE_FIELDS } from "../../utils/dashboardWidgetFontSizes";
import {
  DEFAULT_REPORT_PRIORITY_POLICY,
  normalizeReportPriorityPolicy,
  REPORT_PRIORITY_POLICIES,
} from "../../utils/reportPriorityPolicy";

export const createBlankReportType = () => ({ value: "", label: "", apiUrl: "", params: [] });
export const createBlankParam = () => ({ key: "", label: "", required: true, placeholder: "", inputType: "text", options: [], defaultValue: "", system: false });
export const createBlankAssetCategory = () => ({ value: "", label: "", assetTypes: [] });
export const createBlankVoiceAction = () => ({
  id: `voice-${Date.now()}`,
  label: "New media command",
  phrases: ["hello sama", "hey sama", "مرحبا سما"],
  action: { type: "media", media: { path: "", title: "", mediaType: "image", videoEndBehavior: "close" } },
});

export const normalizeAiReportAvailability = (value = {}) => ({
  devices: value.devices !== false,
  dashboards: value.dashboards !== false,
  templates: value.templates !== false,
});

export const normalizeNewSubscriberPermissionExclusions = (value = {}) => ({
  excludedPermissionIds: Array.from(
    new Set(
      (Array.isArray(value?.excludedPermissionIds) ? value.excludedPermissionIds : [])
        .map((item) => String(item || "").trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b)),
});

export const REPORT_PRIORITY_POLICY_OPTIONS = [
  {
    value: REPORT_PRIORITY_POLICIES.SHIFT,
    title: "Shift remaining priorities",
    description: "Assign the requested priority, then increment the occupant and any consecutive conflicts until an unused priority is reached.",
  },
  {
    value: REPORT_PRIORITY_POLICIES.SWAP,
    title: "Swap conflicting priorities",
    description: "Exchange the edited datastream with the datastream currently occupying that priority.",
  },
  {
    value: REPORT_PRIORITY_POLICIES.REJECT,
    title: "Reject duplicates",
    description: "Keep the current order and show an error until the user chooses an unused priority.",
  },
];

export const normalizeGoogleSheetsAssetSync = (value = {}) => ({
  enabled: value.enabled !== false,
  pollIntervalSeconds: Math.min(300, Math.max(10, Number(value.pollIntervalSeconds) || 15)),
  subscribers: (Array.isArray(value.subscribers) ? value.subscribers : []).map((item) => ({
    subscriberId: Number(item.subscriberId),
    enabled: item.enabled !== false,
    spreadsheetId: String(item.spreadsheetId || ""),
    sheetName: String(item.sheetName || "Assets"),
    syncState: ["connected", "error"].includes(item.syncState) ? item.syncState : null,
    lastSyncedAt: item.lastSyncedAt || null,
    lastAttemptAt: item.lastAttemptAt || null,
    lastError: item.lastError || null,
    dataVersion: Number(item.dataVersion || 0),
    lastSummary: item.lastSummary || null,
  })),
});

export const normalizeReportTypes = (value) =>
  (Array.isArray(value?.types) ? value.types : []).map((type) => ({
    value: String(type?.value || ""),
    label: String(type?.label || ""),
    apiUrl: normalizeAiReportApiUrl(type?.apiUrl),
    params: (Array.isArray(type?.params) ? type.params : []).map((param) => ({
      key: String(param?.key || ""),
      label: String(param?.label || ""),
      required: param?.required !== false,
      placeholder: String(param?.placeholder || ""),
      inputType: normalizeAiReportParamInputType(param?.inputType ?? param?.type),
      options: normalizeAiReportParamOptions(param?.options ?? param?.values),
      defaultValue: param?.defaultValue === undefined || param?.defaultValue === null ? "" : String(param.defaultValue),
      system: param?.system === true || param?.hidden === true,
    })),
  }));

export const serializeOptions = (options = []) =>
  normalizeAiReportParamOptions(options)
    .map((option) => option.value)
    .join(", ");

export const normalizeMode = (mode) =>
  mode === "anovator_redirect" ? "anovator_redirect" : "internal_pdf";

export const modeIcons = {
  internal_pdf: <PictureAsPdfIcon />,
  anovator_redirect: <OpenInNewIcon />,
};

export const DEFAULT_WEATHER_CRON_CONFIG = {
  enabled: true,
  intervalMinutes: 30,
  timezone: "Asia/Dubai",
  requestTimeoutMs: 10000,
  providers: [
    { id: "open_meteo_air_quality", enabled: true, role: "air_quality" },
    { id: "open_meteo_forecast", enabled: true, role: "weather" },
    { id: "met_no_forecast", enabled: true, role: "weather" },
    { id: "openweathermap_air_pollution", enabled: false, role: "air_quality", apiKeyEnv: "OPENWEATHER_API_KEY" },
  ],
  thresholds: {
    lightDustUgM3: 5,
    moderateDustUgM3: 20,
    heavyDustUgM3: 50,
    sandstormDustUgM3: 150,
    dustyPm10UgM3: 100,
    hazyAerosolOpticalDepth: 0.6,
  },
};

export const DEFAULT_FCM_TOKEN_CLEANUP_CONFIG = {
  enabled: true,
  intervalHours: 3,
};

export const WEATHER_PROVIDER_LABELS = {
  open_meteo_air_quality: "Open-Meteo Air Quality",
  open_meteo_forecast: "Open-Meteo Forecast",
  met_no_forecast: "MET Norway Forecast",
  openweathermap_air_pollution: "OpenWeather Air Pollution",
};

export const WEATHER_THRESHOLD_FIELDS = [
  { key: "lightDustUgM3", label: "Light dust", unit: "ug/m3" },
  { key: "moderateDustUgM3", label: "Moderate dust", unit: "ug/m3" },
  { key: "heavyDustUgM3", label: "Heavy dust", unit: "ug/m3" },
  { key: "sandstormDustUgM3", label: "Sandstorm", unit: "ug/m3" },
  { key: "dustyPm10UgM3", label: "Dusty PM10", unit: "ug/m3" },
  { key: "hazyAerosolOpticalDepth", label: "Hazy AOD", unit: "AOD" },
];

export const normalizeNumber = (value, fallback, { min = Number.NEGATIVE_INFINITY, max = Number.POSITIVE_INFINITY } = {}) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, parsed));
};

export const normalizeWeatherCronConfig = (value = {}) => {
  const providerMap = new Map(DEFAULT_WEATHER_CRON_CONFIG.providers.map((provider) => [provider.id, { ...provider }]));
  (Array.isArray(value.providers) ? value.providers : []).forEach((provider) => {
    const id = String(provider?.id || "").trim();
    if (!id) return;
    providerMap.set(id, {
      ...(providerMap.get(id) || {}),
      id,
      enabled: provider.enabled !== false,
      role: provider.role === "weather" ? "weather" : "air_quality",
      apiKeyEnv: typeof provider.apiKeyEnv === "string" ? provider.apiKeyEnv.trim() : providerMap.get(id)?.apiKeyEnv,
      apiKey: typeof provider.apiKey === "string" ? provider.apiKey.trim() : undefined,
    });
  });

  const thresholds = { ...DEFAULT_WEATHER_CRON_CONFIG.thresholds, ...(value.thresholds || {}) };

  return {
    ...DEFAULT_WEATHER_CRON_CONFIG,
    ...value,
    enabled: value.enabled !== false,
    intervalMinutes: normalizeNumber(value.intervalMinutes, DEFAULT_WEATHER_CRON_CONFIG.intervalMinutes, { min: 15 }),
    requestTimeoutMs: normalizeNumber(value.requestTimeoutMs, DEFAULT_WEATHER_CRON_CONFIG.requestTimeoutMs, { min: 1000 }),
    timezone: String(value.timezone || DEFAULT_WEATHER_CRON_CONFIG.timezone),
    providers: Array.from(providerMap.values()),
    thresholds: WEATHER_THRESHOLD_FIELDS.reduce((acc, field) => {
      acc[field.key] = normalizeNumber(thresholds[field.key], DEFAULT_WEATHER_CRON_CONFIG.thresholds[field.key], { min: 0 });
      return acc;
    }, {}),
  };
};

export const normalizeFcmTokenCleanupConfig = (value = {}) => ({
  ...DEFAULT_FCM_TOKEN_CLEANUP_CONFIG,
  ...value,
  enabled: value.enabled !== false,
  intervalHours: normalizeNumber(value.intervalHours, DEFAULT_FCM_TOKEN_CLEANUP_CONFIG.intervalHours, { min: 1 }),
});

export const WIDGET_PREVIEW_DEFAULT_PROP_KEYS = {
  chart: "defaultChartProps",
  "period-column": "previewPeriodColumnProps",
  gauge: "defaultGaugeProps",
  "radial-gauge": "defaultRadialGaugeProps",
  label: "defaultLabelProps",
  led: "defaultLEDProps",
  number: "defaultNumberProps",
  slider: "defaultSliderProps",
  switch: "defaultSwitchProps",
  "push-button": "defaultPushButtonProps",
  terminal: "defaultTerminalProps",
  text: "defaultTextProps",
  menu: "defaultMenuProps",
  "segment-switch": "defaultSegmentSwitchProps",
  "image-button": "defaultImageButtonProps",
  keypad: "defaultKeypadProps",
  table: "defaultTableProps",
  fan: "defaultFanProps",
  pipe: "defaultPipeProps",
  elbow: "defaultElbowProps",
  "water-pump": "defaultWaterPumpProps",
  liquid: "defaultLiquidProps",
  "water-tank": "defaultWaterTankProps",
  "text-title": "defaultTextTitleProps",
  "camera-stream": "defaultCameraStreamProps",
  "time-range": "defaultTimeRangeProps",
  "environmental-status": "defaultEnvironmentalStatusProps",
  "asset-status": "defaultAssetStatusProps",
  "status-arrow": "defaultStatusArrowProps",
};

export const WIDGET_PREVIEW_TITLES = {
  chart: "Chart",
  "period-column": "Period Column",
  gauge: "Gauge",
  "radial-gauge": "Radial Gauge",
  label: "Label",
  led: "LED",
  number: "Number",
  slider: "Slider",
  switch: "Switch",
  "push-button": "Push Button",
  terminal: "Terminal",
  text: "Text",
  menu: "Menu",
  "segment-switch": "Segment Switch",
  "image-button": "Image Button",
  keypad: "Keypad",
  table: "Table",
  fan: "Fan",
  pipe: "Pipe",
  elbow: "Elbow",
  "water-pump": "Water Pump",
  liquid: "Liquid",
  "water-tank": "Water Tank",
  "text-title": "Title Widget",
  "camera-stream": "Camera Stream",
  "time-range": "Digital Clock",
  "environmental-status": "Environmental Status",
  "asset-status": "Asset Status",
  "status-arrow": "Status Arrow",
};

export const WIDGET_PREVIEW_SIZES = {
  chart: { width: 360, height: 240 },
  "period-column": { width: 360, height: 240 },
  gauge: { width: 260, height: 240 },
  "radial-gauge": { width: 260, height: 240 },
  liquid: { width: 240, height: 240 },
  "water-tank": { width: 240, height: 240 },
  label: { width: 320, height: 150 },
  led: { width: 220, height: 150 },
  number: { width: 280, height: 180 },
  slider: { width: 320, height: 150 },
  switch: { width: 280, height: 150 },
  "push-button": { width: 280, height: 150 },
  terminal: { width: 340, height: 190 },
  text: { width: 300, height: 150 },
  "text-title": { width: 320, height: 120 },
  menu: { width: 280, height: 150 },
  "segment-switch": { width: 320, height: 150 },
  "image-button": { width: 240, height: 160 },
  keypad: { width: 260, height: 260 },
  table: { width: 360, height: 240 },
  fan: { width: 240, height: 220 },
  pipe: { width: 340, height: 170 },
  elbow: { width: 260, height: 220 },
  "water-pump": { width: 320, height: 180 },
  "time-range": { width: 360, height: 190 },
  "environmental-status": { width: 360, height: 260 },
  "asset-status": { width: 360, height: 230 },
  "status-arrow": { width: 320, height: 150 },
};

export const WIDGET_PREVIEW_DATASTREAMS = [
  { id: 9001, pin: "V1", name: "Temperature", value: 32, defaultValue: 32, min: 0, max: 100, unit: "C", decimals: 0 },
  { id: 9002, pin: "V2", name: "Humidity", value: 64, defaultValue: 64, min: 0, max: 100, unit: "%", decimals: 0 },
  { id: 9003, pin: "V3", name: "Air Quality", value: 42, defaultValue: 42, min: 0, max: 100, unit: "ppm", decimals: 0 },
];

export const AUDIT_LOG_ACTION_OPTIONS = [
  {
    action: "create",
    label: "Create",
    description: "Store logs when records are created.",
    defaultEnabled: true,
  },
  {
    action: "update",
    label: "Update",
    description: "Store logs when records are changed.",
    defaultEnabled: true,
  },
  {
    action: "delete",
    label: "Delete",
    description: "Store logs when records are deleted.",
    defaultEnabled: true,
  },
  {
    action: "import",
    label: "Import",
    description: "Store bulk import logs.",
    defaultEnabled: true,
  },
  {
    action: "export",
    label: "Export",
    description: "Store export/download logs.",
    defaultEnabled: true,
  },
  {
    action: "access_denied",
    label: "Access denied",
    description: "Store security and permission denial logs.",
    defaultEnabled: true,
  },
  {
    action: "read",
    label: "Read",
    description: "Normal page/detail read events. Keep disabled unless you need full traceability.",
    defaultEnabled: false,
  },
  {
    action: "read_history",
    label: "Read history",
    description: "History reads. Usually noisy, so it is disabled by default.",
    defaultEnabled: false,
  },
  {
    action: "read_log",
    label: "Read log",
    description: "Audit-log/detail reads. Disabled by default to avoid recursive log noise.",
    defaultEnabled: false,
  },

  {
    action: "list",
    label: "List",
    description: "List/table fetch events. Disabled by default because they are read-only and high-volume.",
    defaultEnabled: false,
  },
  {
    action: "view",
    label: "View",
    description: "Generic view events. Disabled by default because they are read-only.",
    defaultEnabled: false,
  },
  {
    action: "download",
    label: "Download",
    description: "Read-only download events. Enable only when download traceability is required.",
    defaultEnabled: false,
  },
];

export const DEFAULT_AUDIT_LOG_POLICY = {
  enabled: true,
  saveUnknownActions: true,
  actions: AUDIT_LOG_ACTION_OPTIONS.map(({ action, defaultEnabled }) => ({ action, enabled: defaultEnabled })),
};

export const normalizeAuditLogPolicy = (value = {}) => {
  const actionMap = new Map(
    (Array.isArray(value.actions) ? value.actions : [])
      .map((item) => [String(item?.action || item?.key || item?.value || "").trim(), item?.enabled !== false])
      .filter(([action]) => action)
  );

  return {
    enabled: value.enabled !== false,
    saveUnknownActions: value.saveUnknownActions !== false,
    actions: AUDIT_LOG_ACTION_OPTIONS.map(({ action, defaultEnabled }) => ({
      action,
      enabled: actionMap.has(action) ? actionMap.get(action) : defaultEnabled,
    })),
  };
};

export {
  DEFAULT_REPORT_PRIORITY_POLICY,
  REPORT_PRIORITY_POLICIES,
  normalizeReportPriorityPolicy,
  normalizeStringList,
  normalizeVoiceCommandConfig,
  DASHBOARD_WIDGET_FONT_SIZE_FIELDS,
};
